import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "@/App";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { getTicketPurchaseDestination } from "@/utils/ticketPurchase";

const PaymentSuccess = ({ user, onLogout }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paymentType = searchParams.get("type");
  const eventId = searchParams.get("event_id");
  const isTithe = paymentType === "tithe" && Boolean(eventId);
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    if (attempts >= 30) {
      setChecking(false);
      return;
    }

    try {
      const response = await axiosInstance.get(`/payments/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        if (response.data.payment_type === "ticket") {
          navigate(
            getTicketPurchaseDestination(response.data.event_id, response.data.event_status),
            { replace: true },
          );
          return;
        }
        setVerified(true);
        setChecking(false);
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment:", error);
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f]">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-24 px-4 flex items-center justify-center min-h-[80vh]">
        <div className="text-center max-w-md">
          {checking ? (
            <div>
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-white text-2xl font-bold mb-2">Processing Payment...</h2>
              <p className="text-gray-400">Please wait while we confirm your payment</p>
            </div>
          ) : verified ? (
            <div className="animate-fadeIn">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h2 className="text-white text-3xl font-bold mb-4">Payment Successful!</h2>
              <p className="text-gray-400 mb-4">
                {isTithe ? "Your contribution was confirmed securely by Stripe." : "Thank you for your purchase"}
              </p>
              <p className="text-gray-500 text-sm mb-8">
                {isTithe ? "Your payment receipt is available from Stripe." : "You can now add this event to your calendar"}
              </p>
              <div className="flex flex-col gap-4 max-w-sm mx-auto">
                <Button
                  onClick={() => navigate(
                    isTithe
                      ? `/event/${eventId}`
                      : (user?.role === "creator" ? "/creator/dashboard" : "/my-tickets"),
                  )}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
                >
                  {isTithe
                    ? "Return to Live Stream"
                    : (user?.role === "creator" ? "Go to Dashboard" : "View My Tickets & Add to Calendar")}
                </Button>
                {!isTithe && (
                  <Button
                    onClick={() => navigate("/browse")}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold"
                  >
                    Browse More Events
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-white text-2xl font-bold mb-2">Payment Processing</h2>
              <p className="text-gray-400 mb-6">Your payment is being processed. You'll receive a confirmation email shortly.</p>
              <Button
                onClick={() => navigate("/events")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
              >
                Return Home
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
